import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCustomer } from '@/context/CustomerContext';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { User, Package, MapPin, LogOut, Plus, Trash2, ShoppingCart, Pencil } from 'lucide-react';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { US_STATES, CA_PROVINCES, COUNTRIES } from '@/utils/locationData';
export default function CustomerAccount() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { customer, loading, logout, updateProfile, getOrders, updateAddresses } = useCustomer();
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);

    const [isAddingAddress, setIsAddingAddress] = useState(false);
    const [editingAddressIndex, setEditingAddressIndex] = useState(null);
    const [addressForm, setAddressForm] = useState({
        firstname: '',
        lastname: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zipcode: '',
        country: 'US',
        is_default: false
    });

    useEffect(() => {
        if (!loading && !customer) {
            navigate('/customer/login');
        }
    }, [customer, loading, navigate]);

    useEffect(() => {
        if (customer) {
            fetchOrders();
        }
    }, [customer]);


    const fetchOrders = async () => {
        setLoadingOrders(true);
        try {
            const data = await getOrders();
            // Defensively cast to array to avoid .map crash if object / undefined is returned
            if (Array.isArray(data)) setOrders(data);
            else if (data && Array.isArray(data.orders)) setOrders(data.orders);
            else if (data && Array.isArray(data.data)) setOrders(data.data);
            else setOrders([]);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingOrders(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    // Show a spinner while the customer context is loading from localStorage token
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="flex flex-col items-center gap-3 text-slate-500">
                    <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm">Loading your account...</p>
                </div>
            </div>
        );
    }

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        try {
            await updateProfile({
                name: formData.get('name'),
                email: formData.get('email'),
                phone: formData.get('phone')
            });
            toast.success("Profile updated");
        } catch (error) {
            toast.error("Update failed");
        }
    };

    const handleAddAddress = async (e) => {
        e.preventDefault();
        try {
            let newAddresses = [...(customer.addresses || [])];
            if (editingAddressIndex !== null) {
                newAddresses[editingAddressIndex] = addressForm;
            } else {
                newAddresses = [...newAddresses, addressForm];
            }

            await updateAddresses(newAddresses);
            toast.success(editingAddressIndex !== null ? "Address updated successfully" : "Address added successfully");
            setIsAddingAddress(false);
            setEditingAddressIndex(null);
            setAddressForm({
                firstname: '', lastname: '', phone: '', address: '',
                city: '', state: '', zipcode: '', country: 'US', is_default: false
            });
        } catch (err) {
            toast.error("Failed to save address");
        }
    };

    const handleEditAddressClick = (index, address) => {
        setAddressForm(address);
        setEditingAddressIndex(index);
        setIsAddingAddress(true);
    };

    const handleRemoveAddress = async (index) => {
        try {
            const newAddresses = [...customer.addresses];
            newAddresses.splice(index, 1);
            await updateAddresses(newAddresses);
            toast.success("Address removed");
        } catch (err) {
            toast.error("Failed to remove address");
        }
    };

    const handleSetDefaultAddress = async (index) => {
        try {
            const newAddresses = customer.addresses.map((a, i) => ({
                ...a,
                is_default: i === index
            }));
            await updateAddresses(newAddresses);
            toast.success("Default address updated");
        } catch (err) {
            toast.error("Failed to set default address");
        }
    };

    if (!customer) return null;

    return (
        <div className="min-h-screen bg-[#FDFDFD] flex flex-col font-sans animate-fade-in">
            <Header />

            <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12 mt-16">
                <div className="flex items-center justify-between mb-8">
                    <h1 className="text-3xl font-bold text-slate-900">My Account</h1>
                    <Button variant="outline" onClick={handleLogout} className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
                        <LogOut size={16} className="mr-2" />
                        Sign Out
                    </Button>
                </div>

                <Tabs defaultValue="orders" className="space-y-8">
                    <TabsList className="bg-slate-100 p-1 rounded-xl">
                        <TabsTrigger value="orders" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm">
                            <Package size={16} className="mr-2" />
                            My Orders
                        </TabsTrigger>
                        <TabsTrigger value="profile" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm">
                            <User size={16} className="mr-2" />
                            Profile
                        </TabsTrigger>
                        <TabsTrigger value="addresses" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm">
                            <MapPin size={16} className="mr-2" />
                            Addresses
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="orders">
                        <Card className="border-slate-200 shadow-sm">
                            <CardHeader>
                                <CardTitle>Order History</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {loadingOrders ? (
                                    <div className="py-12 flex justify-center">
                                        <div className="w-8 h-8 border-4 border-amber-700 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                ) : orders.length === 0 ? (
                                    <div className="text-center py-12">
                                        <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
                                            <Package size={24} />
                                        </div>
                                        <h3 className="text-lg font-medium text-slate-900">No orders yet</h3>
                                        <p className="text-slate-500 mt-1">Start shopping to see your orders here.</p>
                                        <Button
                                            onClick={() => navigate('/products')}
                                            className="mt-6 bg-amber-700 hover:bg-amber-800 text-white"
                                        >
                                            Browse Shop
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <Accordion type="multiple" defaultValue={searchParams.get('order') ? [`order-${searchParams.get('order')}`] : []} className="w-full space-y-4">
                                            {orders.map((order, orderIndex) => (
                                                <AccordionItem key={order._id} value={`order-${order.order_id}`} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                                                    <AccordionTrigger className="px-6 py-4 hover:bg-slate-50 hover:no-underline transition-colors flex justify-between">
                                                        <div className="flex flex-col md:flex-row md:items-center justify-between w-full pr-4 gap-4">
                                                            <div className="text-left">
                                                                <p className="font-bold text-slate-900">{order.order_number}</p>
                                                                <p className="text-sm text-slate-500">{new Date(order.created_at).toLocaleDateString()}</p>
                                                            </div>
                                                            <div className="flex items-center gap-6">
                                                                <div className="text-right hidden sm:block">
                                                                    <p className="text-sm text-slate-500">Total</p>
                                                                    <p className="font-bold text-slate-900">${order.total?.toFixed(2)}</p>
                                                                </div>
                                                                <div className="text-right">
                                                                    <p className="text-sm text-slate-500 mb-1">Status</p>
                                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                                                                        {order.status}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </AccordionTrigger>
                                                    <AccordionContent className="px-6 pb-6 pt-2 border-t border-slate-100">
                                                        <div className="space-y-4">
                                                            {/* Products List */}
                                                            <div className="space-y-4">
                                                                {order.items?.map((item, index) => (
                                                                    <div key={index} className="flex gap-4">
                                                                        <div className="w-16 h-16 rounded-md bg-slate-50 border border-slate-100 overflow-hidden flex-shrink-0">
                                                                            {item.image_url ? (
                                                                                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                                                            ) : (
                                                                                <div className="w-full h-full flex items-center justify-center text-slate-300">
                                                                                    <ShoppingCart size={20} />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div className="flex-1 min-w-0 flex flex-col justify-center">
                                                                            <p className="text-sm font-medium text-slate-900 line-clamp-2">{item.name}</p>
                                                                            <p className="text-sm text-slate-500 mt-1">Qty: {item.quantity}</p>

                                                                            {/* Customizations display */}
                                                                            {item.customizations && (Array.isArray(item.customizations) ? item.customizations.length > 0 : Object.keys(item.customizations).length > 0) && (
                                                                                <div className="mt-2 space-y-1 pl-2 border-l border-slate-100 py-0.5">
                                                                                    {Array.isArray(item.customizations) ? (
                                                                                        item.customizations.map((cust) => (
                                                                                            <div key={cust.id} className="text-[10px]">
                                                                                                <div className="flex justify-between font-bold text-slate-600">
                                                                                                    <span>{cust.name}</span>
                                                                                                    <span className="text-amber-800 ml-2">+${cust.total.toFixed(2)}</span>
                                                                                                </div>
                                                                                                {cust.fields.map((f, i) => (
                                                                                                    <p key={i} className="text-slate-400 italic leading-tight text-[10px]">
                                                                                                        {f.label}: {f.value}
                                                                                                    </p>
                                                                                                ))}
                                                                                            </div>
                                                                                        ))
                                                                                    ) : (
                                                                                        Object.entries(item.customizations).map(([label, val]) => (
                                                                                            <p key={label} className="text-[10px] text-slate-400 italic">
                                                                                                <span className="font-medium mr-1 text-slate-500">{label}:</span>
                                                                                                {val && typeof val === 'string' && val.includes('T') && !isNaN(Date.parse(val))
                                                                                                    ? new Date(val).toLocaleDateString()
                                                                                                    : val === true ? 'Yes' : val}
                                                                                            </p>
                                                                                        ))
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div className="text-right flex flex-col justify-center flex-shrink-0">
                                                                            <span className="font-medium text-slate-900">
                                                                                ${((item.display_price || item.base_price || 0) * item.quantity).toFixed(2)}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>

                                                            {/* Cost Breakdown */}
                                                            <div className="border-t border-slate-100 pt-4 space-y-2 text-sm">
                                                                <div className="flex justify-between text-slate-600">
                                                                    <span>Subtotal</span>
                                                                    <span className="font-medium text-slate-900">${(order.subtotal || order.base_total || 0).toFixed(2)}</span>
                                                                </div>
                                                                <div className="flex justify-between text-slate-600">
                                                                    <span className="flex flex-col">
                                                                        <span>Shipping</span>
                                                                        {order.shipping_method && <span className="text-xs text-slate-400">{order.shipping_method}</span>}
                                                                    </span>
                                                                    <span className="font-medium text-slate-900">${(order.shipping || order.shipping_cost || 0).toFixed(2)}</span>
                                                                </div>
                                                                {order.tax > 0 && (
                                                                    <div className="flex justify-between text-slate-600">
                                                                        <span>Tax</span>
                                                                        <span className="font-medium text-slate-900">${order.tax?.toFixed(2)}</span>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="border-t border-slate-100 flex justify-between items-center bg-slate-50 -mx-6 px-6 py-4 mt-4 mb-[-24px]">
                                                                <span className="font-bold text-slate-900 text-base">Total</span>
                                                                <span className="font-bold text-indigo-600 text-xl">${(order.total_charged || order.total || 0).toFixed(2)}</span>
                                                            </div>

                                                        </div>
                                                    </AccordionContent>
                                                </AccordionItem>
                                            ))}
                                        </Accordion>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="profile">
                        <Card className="border-slate-200 shadow-sm max-w-2xl">
                            <CardHeader>
                                <CardTitle>Profile Details</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleUpdateProfile} className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Full Name</Label>
                                            <Input name="name" defaultValue={customer.name} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Phone Number</Label>
                                            <Input name="phone" defaultValue={customer.phone} />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Email Address</Label>
                                        <Input name="email" type="email" defaultValue={customer.email} disabled className="bg-slate-50" />
                                        <p className="text-xs text-slate-500">Email cannot be changed directly.</p>
                                    </div>
                                    <Button type="submit" className="bg-amber-700 hover:bg-amber-800 text-white">Save Changes</Button>
                                </form>
                            </CardContent>
                        </Card>

                        <Card className="border-slate-200 shadow-sm max-w-2xl mt-8">
                            <CardHeader>
                                <CardTitle>Change Password</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={async (e) => {
                                    e.preventDefault();
                                    const formData = new FormData(e.target);
                                    const password = formData.get('password');
                                    const confirm = formData.get('confirmPassword');
                                    if (password !== confirm) {
                                        toast.error("Passwords do not match");
                                        return;
                                    }
                                    if (password.length < 6) {
                                        toast.error("Password must be at least 6 characters");
                                        return;
                                    }
                                    try {
                                        await updateProfile({ password });
                                        toast.success("Password updated successfully");
                                        e.target.reset();
                                    } catch (error) {
                                        toast.error("Failed to update password");
                                    }
                                }} className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>New Password</Label>
                                        <Input name="password" type="password" placeholder="••••••••" required minLength={6} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Confirm New Password</Label>
                                        <Input name="confirmPassword" type="password" placeholder="••••••••" required minLength={6} />
                                    </div>
                                    <Button type="submit" variant="outline" className="border-amber-700 text-amber-700 hover:bg-amber-50">Update Password</Button>
                                </form>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="addresses">
                        <Card className="border-slate-200 shadow-sm">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>Saved Addresses</CardTitle>
                                {!isAddingAddress && (
                                    <Button onClick={() => {
                                        setEditingAddressIndex(null);
                                        setAddressForm({
                                            firstname: '', lastname: '', phone: '', address: '',
                                            city: '', state: '', zipcode: '', country: 'US', is_default: false
                                        });
                                        setIsAddingAddress(true);
                                    }} size="sm" className="bg-amber-700 hover:bg-amber-800 text-white">
                                        <Plus size={16} className="mr-2" /> Add New
                                    </Button>
                                )}
                            </CardHeader>
                            <CardContent>
                                {isAddingAddress ? (
                                    <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 mb-6">
                                        <h3 className="font-medium text-slate-900 mb-4">{editingAddressIndex !== null ? 'Edit Address' : 'Add New Address'}</h3>
                                        <form onSubmit={handleAddAddress} className="space-y-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label>First Name</Label>
                                                    <Input
                                                        required
                                                        value={addressForm.firstname}
                                                        onChange={e => setAddressForm({ ...addressForm, firstname: e.target.value })}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Last Name</Label>
                                                    <Input
                                                        required
                                                        value={addressForm.lastname}
                                                        onChange={e => setAddressForm({ ...addressForm, lastname: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Phone</Label>
                                                <Input
                                                    required
                                                    value={addressForm.phone}
                                                    onChange={e => setAddressForm({ ...addressForm, phone: e.target.value })}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Street Address</Label>
                                                <Input
                                                    required
                                                    value={addressForm.address}
                                                    onChange={e => setAddressForm({ ...addressForm, address: e.target.value })}
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label>City</Label>
                                                    <Input
                                                        required
                                                        value={addressForm.city}
                                                        onChange={e => setAddressForm({ ...addressForm, city: e.target.value })}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>{addressForm.country === 'CA' ? 'Province' : 'State'}</Label>
                                                    <Select
                                                        value={addressForm.state}
                                                        onValueChange={(val) => setAddressForm({ ...addressForm, state: val })}
                                                    >
                                                        <SelectTrigger className="w-full">
                                                            <SelectValue placeholder={addressForm.country === 'CA' ? 'Province' : 'State'} />
                                                        </SelectTrigger>
                                                        <SelectContent className="max-h-[300px]">
                                                            {(addressForm.country === 'CA' ? CA_PROVINCES : US_STATES).map((state) => (
                                                                <SelectItem key={state.value} value={state.value}>
                                                                    {state.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label>ZIP Code</Label>
                                                    <Input
                                                        required
                                                        value={addressForm.zipcode}
                                                        onChange={e => setAddressForm({ ...addressForm, zipcode: e.target.value })}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Country</Label>
                                                    <Select
                                                        value={addressForm.country}
                                                        onValueChange={(val) => setAddressForm({ ...addressForm, country: val, state: '' })}
                                                    >
                                                        <SelectTrigger className="w-full">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {COUNTRIES.map(c => (
                                                                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                            <div className="flex gap-2 pt-2">
                                                <Button type="submit" className="bg-amber-700 hover:bg-amber-800 text-white">{editingAddressIndex !== null ? 'Update Address' : 'Save Address'}</Button>
                                                <Button type="button" variant="outline" onClick={() => {
                                                    setIsAddingAddress(false);
                                                    setEditingAddressIndex(null);
                                                }}>Cancel</Button>
                                            </div>
                                        </form>
                                    </div>
                                ) : null}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {(customer.addresses || []).map((address, index) => (
                                        <div key={index} className={`border rounded-xl p-4 flex flex-col ${address.is_default ? 'border-amber-700 bg-amber-50/50' : 'border-slate-200'}`}>
                                            <div className="flex justify-between items-start mb-2">
                                                <h4 className="font-medium text-slate-900">
                                                    {address.firstname} {address.lastname}
                                                </h4>
                                                {address.is_default && (
                                                    <span className="text-xs font-bold text-amber-900 bg-amber-100 px-2 py-1 rounded-full text-center">
                                                        Default
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-slate-600 text-sm">{address.address}</p>
                                            <p className="text-slate-600 text-sm">{address.city}, {address.state} {address.zipcode}</p>
                                            <p className="text-slate-500 text-sm mt-1">{address.phone}</p>

                                            <div className="mt-auto pt-4 flex items-center justify-between border-t border-slate-100 mt-4">
                                                {!address.is_default && (
                                                    <button
                                                        onClick={() => handleSetDefaultAddress(index)}
                                                        className="text-sm font-medium text-amber-700 hover:text-amber-800"
                                                    >
                                                        Set as Default
                                                    </button>
                                                )}
                                                <div className="flex items-center gap-2 ml-auto">
                                                    <button
                                                        onClick={() => handleEditAddressClick(index, address)}
                                                        className="p-1.5 text-slate-400 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
                                                        title="Edit Address"
                                                    >
                                                        <Pencil size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleRemoveAddress(index)}
                                                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Delete Address"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            <Footer />
        </div>
    );
}
